// sections.js — shared helpers for BOQ/SOQ sections.
//
// `sectionNo` + `sectionName` live on each soqItem (stamped from the named
// subtotal markers in SOQRegisterSection). These helpers are the single source
// of truth for the section list, labels, filtering and grouping used across the
// downstream modules (Variations, Certificates, CPA, Financial, Materials).

const norm = (v) => String(v == null ? "" : v).trim();

// Stable key for a section ("<no>||<name>"). Matches computeSectionBreakdown.
export function sectionKey(item = {}) {
  return `${norm(item.sectionNo)}||${norm(item.sectionName)}`;
}

// Display label, e.g. "3 — Earthworks", "Earthworks", or "" if neither.
export function sectionLabel(sectionNo, sectionName) {
  return [norm(sectionNo), norm(sectionName)].filter(Boolean).join(" — ");
}

// Ordered, unique sections present on the items.
// → [{ key, sectionNo, sectionName, label }], sorted by number then name.
export function getProjectSections(items = []) {
  const map = new Map();
  (items || []).forEach((it) => {
    const sectionNo = norm(it.sectionNo);
    const sectionName = norm(it.sectionName);
    if (!sectionNo && !sectionName) return;
    const key = `${sectionNo}||${sectionName}`;
    if (!map.has(key)) {
      map.set(key, { key, sectionNo, sectionName, label: sectionLabel(sectionNo, sectionName) });
    }
  });
  return Array.from(map.values()).sort((a, b) =>
    String(a.sectionNo || a.sectionName).localeCompare(
      String(b.sectionNo || b.sectionName), undefined, { numeric: true, sensitivity: "base" }
    )
  );
}

// True if an item matches the chosen filter value: a section key, "All", or
// "__none__" (unsectioned).
export function matchesSection(item = {}, filterValue) {
  if (!filterValue || filterValue === "All") return true;
  if (filterValue === "__none__") return !norm(item.sectionNo) && !norm(item.sectionName);
  return sectionKey(item) === filterValue;
}

// Group items into ordered sections for subheader rendering; unsectioned last.
// → [{ key, sectionNo, sectionName, label, items: [...] }]
export function groupBySection(items = []) {
  const groups = new Map();
  (items || []).forEach((it) => {
    const sectionNo = norm(it.sectionNo);
    const sectionName = norm(it.sectionName);
    const sectioned = sectionNo || sectionName;
    const key = sectioned ? `${sectionNo}||${sectionName}` : "__none__";
    if (!groups.has(key)) {
      groups.set(key, {
        key, sectionNo, sectionName,
        label: sectioned ? sectionLabel(sectionNo, sectionName) : "Unsectioned",
        items: [],
      });
    }
    groups.get(key).items.push(it);
  });
  const all = Array.from(groups.values());
  const sectioned = all.filter((g) => g.key !== "__none__").sort((a, b) =>
    String(a.sectionNo || a.sectionName).localeCompare(
      String(b.sectionNo || b.sectionName), undefined, { numeric: true, sensitivity: "base" }
    )
  );
  const none = all.find((g) => g.key === "__none__");
  return none ? [...sectioned, none] : sectioned;
}
